import { FormAccordion } from '@strata/react-form-section';
import type * as Yup from 'yup';

import { type AppThunk } from 'store';
import {
    type FormSection,
    FormSections,
    type IntakeFormSectionSchemas,
    type IntakeFormSectionStatus,
} from 'store/intakeForm/intakeFormSchema';
import {
    resetState,
    updateActiveSection,
    updateCapturedData,
    updateSectionStatus,
} from 'store/intakeForm/intakeFormSlice';

export const resetForm = (): AppThunk => (dispatch) => {
    dispatch(resetState());
};

export const setActiveSection =
    (section: FormSection): AppThunk =>
    (dispatch) => {
        dispatch(updateActiveSection(section));
    };

export const completeSection =
    (section: FormSection, hasException = false): AppThunk =>
    (dispatch) => {
        const payload = {
            section,
            sectionStatus: {
                progressIndicatorStatus: hasException
                    ? FormAccordion.statuses.WARNING
                    : FormAccordion.statuses.COMPLETED,
            } as IntakeFormSectionStatus,
        };
        dispatch(updateSectionStatus(payload));
    };

export const enableSection =
    (section: FormSection): AppThunk =>
    (dispatch) => {
        const payload = {
            section,
            sectionStatus: {
                progressIndicatorStatus: FormAccordion.statuses.ACTIVE,
            } as IntakeFormSectionStatus,
        };
        dispatch(updateSectionStatus(payload));
    };

export const disableSection =
    (section: FormSection): AppThunk =>
    (dispatch) => {
        const payload = {
            section,
            sectionStatus: {
                isOpen: false,
                progressIndicatorStatus: FormAccordion.statuses.NOT_STARTED,
            } as IntakeFormSectionStatus,
        };
        dispatch(updateSectionStatus(payload));
    };

export const openSection =
    (section: FormSection): AppThunk =>
    (dispatch) => {
        const payload = {
            section,
            sectionStatus: { isOpen: true } as IntakeFormSectionStatus,
        };
        dispatch(updateSectionStatus(payload));
    };

export const closeSection =
    (section: FormSection): AppThunk =>
    (dispatch) => {
        const payload = {
            section,
            sectionStatus: { isOpen: false } as IntakeFormSectionStatus,
        };
        dispatch(updateSectionStatus(payload));
    };

export const goToNextSection =
    (completedSection: FormSection, hasException = false): AppThunk =>
    (dispatch, getState) => {
        const nextSection = (completedSection + 1) as FormSection;

        // Reset sections after next section if completing section 1 after later sections have already been filled out
        if (getState().intakeForm.activeSection !== completedSection) {
            Object.values(FormSections).forEach((section) => {
                if (completedSection < section) {
                    dispatch(disableSection(section));
                    dispatch(
                        updateCapturedData({
                            [section]: {} as Yup.InferType<
                                (typeof IntakeFormSectionSchemas)[typeof section]
                            >,
                        }),
                    );
                }
            });
        }

        dispatch(closeSection(completedSection));
        dispatch(completeSection(completedSection, hasException));
        dispatch(openSection(nextSection));
        dispatch(enableSection(nextSection));
        dispatch(setActiveSection(nextSection));
    };
